import dayjs, { Dayjs } from 'dayjs';
import { MarketMetric, MarketProduct, DateStatus, MarketSectionResults } from 'generated/graphql';

/**
 * SRP: Business logic for availability isolated from UI
 */
export const checkProductAvailability = (date: Dayjs, metric: MarketMetric, product: MarketProduct): boolean => {
  const availability = product?.metricInfo
    .find((curr) => curr.metric === metric)
    ?.availability?.find((curr) => dayjs(curr.date).isSame(date, 'day'));

  const hasConsignment = (product.consignmentAvailable ?? 0) > 0;
  const isUnavailable = availability?.status === DateStatus.Unavailable;
  const isSoldOut = availability?.status === DateStatus.SoldOut;

  const hasConsignmentOnly = isUnavailable && hasConsignment;

  if (date.isSame(dayjs(), 'day')) {
    return (!isUnavailable && !isSoldOut) || hasConsignmentOnly;
  }

  return !isUnavailable || hasConsignmentOnly;
};

/**
 * Recursively filters the market tree.
 * This uses a non-mutating approach instead of structuredClone.
 */
export const getFilteredMarketSections = (
  sections: MarketSectionResults[],
  date: Dayjs,
  metric: MarketMetric,
): MarketSectionResults[] => {
  if (!sections?.length) return [];
  return sections
    .map((section) => ({
      ...section,
      sellers: (section.sellers ?? [])
        .map((seller) => ({
          ...seller,
          types: (seller.types ?? [])
            .map((type) => ({
              ...type,
              groups: (type.groups ?? [])
                .map((group) => ({
                  ...group,
                  products: (group.products ?? []).filter((p) => checkProductAvailability(date, metric, p)),
                }))
                .filter((group) => group.products.length > 0),
            }))
            .filter((type) => type.groups.length > 0),
        }))
        .filter((seller) => seller.types.length > 0),
    }))
    .filter((section) => section.sellers.length > 0);
};

export const getUniqueProductId = (sellerId: string, productId: string) => {
  return `${sellerId}-${productId}`;
};

/** True when pathname is the buyers market route (not catalog-market). */
export const isBuyersMarketPath = (pathname: string): boolean =>
  pathname.includes('market') && !pathname.includes('catalog-market');
